/* The WORKBENCH landing — FIVE QUEUE TILES in one row (Ale 2026-09-03,
 * mocks-2026-09-03/wave3/landing-v4 variant b).
 *
 * One tile per queue, in the canon order (record keeping · reconciliation
 * · ownership verification · chase & request · other ops): the queue's
 * colour on the top edge, its count large, its name with the help-dot,
 * its one-line hint, and along the bottom the age of its oldest open item
 * ("oldest 6 d" — "overdue 9 d" in the gap treatment when the oldest
 * item is a request past its deadline) with the Enter door. A clear
 * queue keeps its place, dimmed, its count 0 and "clear" for an age.
 * OTHER OPS renders at zero always: it carries the unroutable safety net.
 * No sentences here — the queue's items are read inside the queue.
 *
 * Data path (B1, Ale 2026-09-04): the COUNTS come from the fund ledger
 * (/api/funds/ledger, one sweep) — Record keeping = to book + to decide +
 * to walk, Reconciliation = standing breaks (snoozed included), Ownership
 * verification = awaiting sign-off, Chase & request = owed; the chip is the
 * fund's total_open. /api/workbench/counts still carries the ages, the
 * Other-ops line and the admin segment. Both polled every 15 s in the
 * background. No open-task read.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchWorkbenchCounts, visibleTotal, type SurfaceLine, type WorkbenchCounts } from './api'
import { fetchFundLedgers } from '../ledger'
import { openWords, recordKeepingTotal, sumLedgers, type FundLedgerList } from '../ledgerWords'
import { useListKeys } from '../keyboard/useListKeys'
import { SURFACE_ORDER, SURFACE_SLUGS, type Surface } from './placement'
import FundSelector from '../FundSelector'
import { useFundScope } from '../fundScope'
import { HelpDot } from '../guidance/Guidance'
import { EmptyState, ErrorState } from '../state'
import './workbench.css'

/** The queue's name and its one-line vocabulary, as the mock prints them. */
const QUEUE_LABEL: Record<Surface, string> = {
  record_keeping: 'Record keeping',
  reconciliation: 'Reconciliation',
  ownership_verification: 'Ownership verification',
  chase_request: 'Chase & request',
  other_ops: 'Other ops',
}
const QUEUE_SUB: Record<Surface, string> = {
  record_keeping: 'book from a document',
  reconciliation: 'does it tie out?',
  ownership_verification: 'your sign-off',
  chase_request: 'documents owed',
  other_ops: 'housekeeping',
}

/** Whole days since the queue's oldest open item — the counts' own
 *  figure, else derived from its timestamp; null when the queue is clear. */
function ageDays(line: SurfaceLine, total: number, now: number): number | null {
  if (total <= 0) return null
  if (line.oldest_age_days != null) return line.oldest_age_days
  if (!line.oldest_at) return null
  const t = Date.parse(line.oldest_at)
  return Number.isNaN(t) ? null : Math.max(0, Math.floor((now - t) / 86_400_000))
}

/** The bottom-row age: "oldest 6 d", "overdue 9 d", "clear". */
function ageWords(days: number | null, overdue: boolean, clear: boolean): string {
  if (clear) return 'clear'
  if (days == null) return ''
  const when = days === 0 ? 'today' : `${days} d`
  return `${overdue ? 'overdue' : 'oldest'} ${when}`
}

interface Tile {
  key: Surface
  line: SurfaceLine
  /** The count the officer sees — the line's total minus the cells of
   *  the deferred task types (api.ts visibleTotal). */
  total: number
  clear: boolean
  /** The oldest open item is a request past its deadline. */
  overdue: boolean
  days: number | null
}

export default function WorkbenchHome() {
  const [counts, setCounts] = useState<WorkbenchCounts | null>(null)
  const [ledgers, setLedgers] = useState<FundLedgerList | null>(null)
  const [error, setError] = useState<unknown>(null)
  // Retry (state system): re-runs the effect — a fresh read and a fresh
  // heartbeat clock, never a double fetch.
  const [tick, setTick] = useState(0)
  const [cursor, setCursor] = useState(0)
  /* ONE fund scope, the same on every page (Tier-2 selector ruling): the
   * three fund chips this landing used to carry are gone — the control in
   * the title IS the chooser, and the choice follows the officer into the
   * queue he enters. */
  const { fundId, setFund } = useFundScope()
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false
    const pull = (background = false) => {
      Promise.all([fetchWorkbenchCounts(fundId, background), fetchFundLedgers(background)])
        .then(([c, l]) => {
          if (!cancelled) {
            setCounts(c)
            setLedgers(l)
            setError(null)
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) setError(err)
        })
    }
    pull()
    // Heartbeat (Massimo 2026-08-30): the landing refreshes itself —
    // as background, so the idle clock (lib/api.ts) does not move.
    const t = setInterval(() => pull(true), 15000)
    return () => {
      cancelled = true
      clearInterval(t)
    }
  }, [fundId, tick])
  const retry = () => {
    setError(null)
    setTick((t) => t + 1)
  }

  const enter = useCallback(
    (key: string) => {
      const slug = SURFACE_SLUGS[key as keyof typeof SURFACE_SLUGS]
      navigate(`/v2/workbench/${slug}${fundId ? `?fund=${encodeURIComponent(fundId)}` : ''}`)
    },
    [navigate, fundId]
  )

  /* The ledger rows in scope — one fund's, or every fund's plus the
   * fund-less deck-queue items (a founding decision belongs to no fund
   * yet: it counts on the tile, on no chip). */
  const totals = useMemo(() => {
    if (!ledgers) return null
    const rows = fundId ? ledgers.funds.filter((f) => f.fund_id === fundId) : ledgers.funds
    return sumLedgers(rows, fundId ? undefined : ledgers.unattributed)
  }, [ledgers, fundId])

  /* One tile per queue, in the canon order — never re-sorted. The COUNT
   * is the ledger's; the age, the hint and the admin segment stay the
   * counts endpoint's. */
  const tiles = useMemo<Tile[]>(() => {
    if (!counts || !totals) return []
    const now = Date.parse(counts.generated_at) || Date.now()
    const byKey = new Map(counts.surfaces.map((l) => [l.key, l]))
    const ledgerTotal: Partial<Record<Surface, number>> = {
      record_keeping: recordKeepingTotal(totals),
      reconciliation: totals.breaks.open,
      ownership_verification: totals.awaiting_signoff,
      chase_request: totals.owed.total,
    }
    const out: Tile[] = []
    for (const key of SURFACE_ORDER) {
      const line = byKey.get(key)
      if (!line) continue
      /* The COUNT is the ledger's wherever the ledger has one; the tile
       * the ledger does not cover (other ops) keeps the counts
       * endpoint's visible total. */
      const fromLedger = ledgerTotal[key]
      const total = fromLedger === undefined ? visibleTotal(line) : fromLedger
      const clear = total === 0
      out.push({
        key,
        line,
        total,
        clear,
        overdue: !clear && line.oldest_task_type === 'request_overdue',
        days: ageDays(line, total, now),
      })
    }
    return out
  }, [counts, totals])

  /* The selector's rows: every fund, its ledger's total_open — the count
   * that matters on a page that names no queue — and its open work in
   * clauses under the name. */
  const selectorFunds = useMemo(
    () =>
      (ledgers?.funds ?? []).map((f) => ({
        id: f.fund_id,
        name: f.fund_name,
        open: f.total_open,
        sub: openWords(f),
      })),
    [ledgers]
  )

  /* J / K / Enter over the tiles — the keyboard canon's one hook (A5). A
   * focused tile answers its own Enter; the hook only fires from the page. */
  const onNext = useCallback(
    () => setCursor((c) => Math.min(tiles.length - 1, c + 1)),
    [tiles.length]
  )
  const onPrev = useCallback(() => setCursor((c) => Math.max(0, c - 1)), [])
  const onEnter = useCallback(() => {
    const tile = tiles[Math.min(cursor, tiles.length - 1)]
    if (tile) enter(tile.key)
  }, [tiles, cursor, enter])
  useListKeys({ onNext, onPrev, onEnter, enabled: tiles.length > 0 })

  const allClear = tiles.length > 0 && tiles.every((t) => t.clear)

  return (
    <main className="wb wb-landing" data-testid="workbench">
      <div className="inner">
        <h1>
          Workbench —{' '}
          <FundSelector
            funds={selectorFunds}
            value={fundId}
            onChoose={setFund}
            loading={!ledgers}
          />
        </h1>
        <p className="sub" data-testid="wb-subline">
          Five queues. Pick one to work it.
        </p>
        {error ? <ErrorState what="queue counts" error={error} onRetry={retry} /> : null}

        <div className="qtiles" data-testid="wb-tiles">
          {(counts && totals ? tiles : SURFACE_ORDER.map(() => null)).map((tile, i) => {
            if (!tile) return <div className="qtile skel" key={`skel-${i}`} aria-hidden="true" />
            const { key, total, clear, overdue, days } = tile
            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                data-testid="wb-queue-tile"
                data-surface={key}
                data-total={total}
                data-overdue={overdue || undefined}
                className={`qtile q-${key}${clear ? ' clear' : ''}${i === cursor ? ' cursor' : ''}`}
                onClick={() => enter(key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    enter(key)
                  }
                }}
              >
                <span className="bar" aria-hidden="true" />
                <div className="n" data-testid="wb-queue-tile-count">
                  {total}
                </div>
                <div className="nm" data-testid="wb-queue-tile-name">
                  {QUEUE_LABEL[key]}
                  <HelpDot k={`wb.${key}`} />
                </div>
                <div className="hint">{QUEUE_SUB[key]}</div>
                <div className="ft">
                  <span className={`age${overdue ? ' gap' : ''}`} data-testid="wb-queue-tile-age">
                    {ageWords(days, overdue, clear)}
                  </span>
                  <span className="enter">Enter →</span>
                </div>
              </div>
            )
          })}
        </div>

        {allClear ? (
          <EmptyState
            sentence="All clear — every queue is empty. Nothing is waiting on you."
            door={{ label: 'Drop documents ›', to: '/v2/traces' }}
            testId="wb-all-clear"
          />
        ) : null}
      </div>
    </main>
  )
}
